import React, { useContext } from 'react';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { ValidatedFrequencyCapping, adGroupBidProp, getNetflixFrequencyCapping } from '@bingads-webui-campaign-react/xandr-campaign';
import { isXandrDisplayAdsCampaign } from '@bingads-webui-campaign-react/constants';
import { getConvertedCurrencyDealMinimumBid, getAccountExchangeRateFromLocalStorage } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import { Label } from '@fluentui/react';
import { HelpPopup } from '@bingads-webui-react/help-popup';
import { getBidTitleDetails } from '@bingads-webui-campaign-react/wizard-shared-components';
import { CustomValidatedDefaultBid } from '@bingads-webui-campaign-react/default-bid';

export const XandrBidValue = ({
  data,
  onDataChange,
  legacyI18n,
  isPremiumStreaming,
}) => {
  const i18n = useContext(I18nContext);

  const serverContext = useContext(ServerContext);
  const { campaign, adGroup } = data || {};
  const { deal } = adGroup || {};
  const isDealTypeRestricted = deal && _.isEqual('Restricted', deal.Type);
  const shouldUsePredefinedFrequencyLimit = isDealTypeRestricted;

  const { CampaignSubType, BiddingScheme } = campaign || {};

  const bidType = (BiddingScheme && BiddingScheme.Type === 'ManualCpm') ? 'CpmBid' : 'CpvBid';

  let bidRange = bidType === 'CpmBid' ? serverContext.VideoViewsCPMBidRange : serverContext.VideoViewsCPVBidRange;

  let dealMinimumBid = 0;
  if (deal) {
    const exchangeRateMapping = getAccountExchangeRateFromLocalStorage(serverContext);
    dealMinimumBid = getConvertedCurrencyDealMinimumBid(deal.MinimumBid, deal.Currency, serverContext.CurrentAccount.CurrencyCode, exchangeRateMapping.val);
  }

  if (isXandrDisplayAdsCampaign(CampaignSubType)) {
    bidRange = serverContext.KeywordCPMBidRange;
  }

  const onAdGroupValidate = (result) => {
    if (adGroup) {
      onDataChange({
        data: {
          ...data,
          adGroup: {
            ...data.adGroup,
            ...result.data,
          },
        },
        isValid: result.isValid,
      });
    }
  };

  const bidValidationProps = {
    field: bidType,
    schema: {
      properties: {
        [bidType]: {
          type: 'number',
          minimum: deal ? Math.max(dealMinimumBid, bidRange.FloorValue) : bidRange.FloorValue,
          maximum: deal ? null : bidRange.CeilingValue,
          required: true,
        },
      },
    },
  };

  return (
    <div>
      <ValidationGroup
        validationProps={{
          schema: AdGroup,
          onValidate: onAdGroupValidate,
          data: adGroup || {},
          i18n: legacyI18n,
          stopPropagation: true,
        }}
      >
        <Label htmlFor="input-ad-group-bid">
          {i18n.getString(_TL_('Default bid for bid strategy: {{bidStrategy}}'), { bidStrategy: getBidTitleDetails(i18n, BiddingScheme) })}
          <HelpPopup
            topic={isPremiumStreaming ? 'pop_BA_PremiumStreaming_Bid' : 'pop_BA_BidTypeAwareness'}
            aria-label={i18n.getString(_TL_('Click to learn more about bid strategy'))}
          />
        </Label>
        <CustomValidatedDefaultBid
          placeholderText="25.00"
          id="input-ad-group-bid"
          currencyCode={serverContext.CurrentAccount.CurrencyCode}
          i18n={i18n}
          validationProps={bidValidationProps}
          formErrorProps={{ filter: adGroupBidProp }}
        />
        {
          shouldUsePredefinedFrequencyLimit ?
            getNetflixFrequencyCapping(i18n)
            :
            <ValidatedFrequencyCapping
              i18n={i18n}
              validationProps={{
                data: adGroup.FrequencyCapSettings,
              }}
            />
        }
      </ValidationGroup>
    </div>
  );
};
